/**
 * Reprise d'ouverture 2026 — préparer, contrôler et arrêter le bilan
 * d'ouverture au 01/01/2026.
 *
 * ## La règle que cet écran sert
 *
 * 2025 était tenu en recettes / dépenses. **On ne reconstruit pas les
 * produits 2025 à partir des factures : on établit un bilan d'ouverture au
 * 01/01/2026** (VALIDÉ ÉCLOSION, 26/09/2026 ; AR du 29/04/2019, art. 3:174,
 * 3:175, 3:182 à 3:184). Les créances et crédits des familles sont proposés
 * par calcul depuis les faits lus au 31/12/2025 ; le reste — banque, caisse,
 * fournisseurs, dettes, fonds propres — vient de la comptable et se saisit
 * ici, justifié, ligne par ligne.
 *
 * ## Ce que l'écran n'invente pas
 *
 * Il ne comble jamais l'écart entre actif et passif : il le montre. Tant que
 * les chiffres de la comptable manquent, les lignes restent « en attente » et
 * la reprise ne se valide pas — sans bloquer le reste du travail (notes de
 * transition, lignes clients).
 *
 * ## Quatre onglets, un seul état
 *
 * Synthèse, notes de transition, clients et familles, saisies. Chaque geste
 * recharge la synthèse, les notes et le contrôle clients : les compteurs des
 * onglets disent toujours le travail qui reste.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpenCheck, FileWarning, Loader2, PenLine, Scale, Users } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import clsx from 'clsx';
import { messageErreur } from '../../lib/payments/erreur';
import {
  lireLaReprise,
  lireLaSynthese,
  lireLesComptesDeBilan,
  lireLesEcartsClients,
  lireLesNotesDeTransition,
  lireLesRubriques,
  ouvrirLaReprise,
} from '../../lib/compta/reprise/gateway.supabase';
import { SCHEMA_NON_EXPOSE } from '../../lib/compta/reprise/erreur';
import {
  STATUTS,
  type CompteDeBilan,
  type EcartClient,
  type Exercice,
  type LigneDeSynthese,
  type NoteDeTransition,
  type Reprise,
  type Rubrique,
} from '../../lib/compta/reprise/types';
import { dateCourte } from './components/money';
import SyntheseReprise from './reprise/SyntheseReprise';
import NotesDeTransition from './reprise/NotesDeTransition';
import LignesClients from './reprise/LignesClients';
import SaisiesOuverture from './reprise/SaisiesOuverture';

type Onglet = 'synthese' | 'notes' | 'clients' | 'saisies';

export interface EtatDeLaReprise {
  exercice: Exercice;
  reprise: Reprise;
  rubriques: Rubrique[];
  comptes: CompteDeBilan[];
  synthese: LigneDeSynthese[];
  notes: NoteDeTransition[];
  ecartsClients: EcartClient[];
}

export default function AdminRepriseOuverturePage() {
  const [onglet, setOnglet] = useState<Onglet>('synthese');
  const [exercice, setExercice] = useState<Exercice | null>(null);
  const [reprise, setReprise] = useState<Reprise | null>(null);
  const [rubriques, setRubriques] = useState<Rubrique[]>([]);
  const [comptes, setComptes] = useState<CompteDeBilan[]>([]);
  const [synthese, setSynthese] = useState<LigneDeSynthese[]>([]);
  const [notes, setNotes] = useState<NoteDeTransition[]>([]);
  const [ecartsClients, setEcartsClients] = useState<EcartClient[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouverture, setOuverture] = useState(false);
  const [version, setVersion] = useState(0);

  /* Le référentiel ne change pas pendant la séance : lu une fois. */
  useEffect(() => {
    let vivant = true;
    Promise.all([lireLesRubriques(), lireLesComptesDeBilan()])
      .then(([r, c]) => { if (vivant) { setRubriques(r); setComptes(c); } })
      .catch((e) => { if (vivant) setErreur(messageErreur(e)); });
    return () => { vivant = false; };
  }, []);

  const recharger = useCallback(async () => {
    try {
      const { exercice: ex, reprise: rep } = await lireLaReprise();
      setExercice(ex);
      setReprise(rep);
      const [s, n, e] = await Promise.all([
        rep ? lireLaSynthese(rep.id) : Promise.resolve([] as LigneDeSynthese[]),
        lireLesNotesDeTransition(rep?.id ?? null),
        rep ? lireLesEcartsClients() : Promise.resolve([] as EcartClient[]),
      ]);
      setSynthese(s);
      setNotes(n);
      setEcartsClients(e);
      setErreur(null);
    } catch (e) {
      setErreur(messageErreur(e, 'Reprise illisible'));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { void recharger(); }, [recharger, version]);

  const apresGeste = useCallback(() => setVersion((v) => v + 1), []);

  const ouvrir = async () => {
    if (!exercice) return;
    setOuverture(true);
    try {
      await ouvrirLaReprise(exercice.annee);
      toast.success(`Reprise ${exercice.annee} ouverte`);
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setOuverture(false);
    }
  };

  const compteurs = useMemo(() => {
    const aTrancher = notes.filter((n) => !n.validee || n.categorie === 'needs_review').length;
    /* Les etats a valider et a revoir ne concernent que les lignes calculees ;
       « en attente » ne concerne que les saisies. */
    let clients = 0;
    let saisies = 0;
    for (const s of synthese) {
      clients += s.nbAValider + s.nbARevoir;
      saisies += s.nbEnAttente;
    }
    return { notes: aTrancher, clients: clients + ecartsClients.length, saisies };
  }, [notes, synthese, ecartsClients]);

  const etat: EtatDeLaReprise | null = exercice && reprise
    ? { exercice, reprise, rubriques, comptes, synthese, notes, ecartsClients }
    : null;

  const onglets: { cle: Onglet; label: string; icone: typeof Scale; compte?: number }[] = [
    { cle: 'synthese', label: 'Synthèse et validation', icone: Scale },
    { cle: 'notes', label: 'Notes de transition', icone: FileWarning, compte: compteurs.notes },
    { cle: 'clients', label: 'Clients et familles', icone: Users, compte: compteurs.clients },
    { cle: 'saisies', label: 'Saisies d’ouverture', icone: PenLine, compte: compteurs.saisies },
  ];

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
            <BookOpenCheck className="h-6 w-6 text-indigo-600" />
            Reprise d’ouverture {exercice?.annee ?? ''}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Le bilan d’ouverture au 01/01/{exercice?.annee ?? '2026'} : créances et crédits des familles
            proposés depuis les faits au {reprise ? dateCourte(reprise.dateArrete) : '31/12/2025'}, puis banque,
            caisse, fournisseurs, dettes et fonds propres saisis d’après les chiffres de la comptable. Les
            produits 2025 ne sont pas reconstruits.
          </p>
        </div>
        {reprise && (
          <span className={clsx('rounded-full px-3 py-1 text-sm font-medium', STATUTS[reprise.statut].classe)}>
            {STATUTS[reprise.statut].label}
          </span>
        )}
      </header>

      {erreur && (
        <div className={clsx(
          'rounded-md border p-4 text-sm',
          erreur === SCHEMA_NON_EXPOSE ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-red-300 bg-red-50 text-red-800',
        )}>
          {erreur}
        </div>
      )}

      {chargement && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Lecture de la reprise…
        </div>
      )}

      {!chargement && !erreur && exercice && !reprise && (
        <section className="rounded-lg border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-medium text-gray-900">La reprise {exercice.annee} n’est pas encore ouverte</h2>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">
            Ouvrir la reprise ne comptabilise rien : cela crée le dossier de préparation, en brouillon, où
            les propositions se calculent, les notes de transition se tranchent et les soldes de la
            comptable se saisissent.
          </p>
          <button
            type="button"
            onClick={ouvrir}
            disabled={ouverture}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {ouverture && <Loader2 className="h-4 w-4 animate-spin" />}
            Ouvrir la reprise {exercice.annee}
          </button>
        </section>
      )}

      {!chargement && !erreur && !exercice && (
        <p className="text-sm text-gray-600">Aucun exercice n’existe encore dans le schéma compta.</p>
      )}

      {etat && (
        <>
          <nav className="flex flex-wrap gap-1 border-b border-gray-200">
            {onglets.map((o) => (
              <button
                key={o.cle}
                type="button"
                onClick={() => setOnglet(o.cle)}
                className={clsx(
                  '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium',
                  onglet === o.cle
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800',
                )}
              >
                <o.icone className="h-4 w-4" />
                {o.label}
                {o.compte ? (
                  <span className="rounded-full bg-amber-100 px-2 text-xs text-amber-800">{o.compte}</span>
                ) : null}
              </button>
            ))}
          </nav>

          {onglet === 'synthese' && <SyntheseReprise etat={etat} apresGeste={apresGeste} />}
          {onglet === 'notes' && <NotesDeTransition etat={etat} apresGeste={apresGeste} />}
          {onglet === 'clients' && <LignesClients etat={etat} apresGeste={apresGeste} version={version} />}
          {onglet === 'saisies' && <SaisiesOuverture etat={etat} apresGeste={apresGeste} version={version} />}
        </>
      )}
    </div>
  );
}
